import classNames from "classnames";
import { FC } from "react";
import { MdBolt } from "react-icons/md";

import { TrashSvg } from "../../../../components/svg/TrashSvg";
import {
  AUTOMATION_ACTION_KEY,
  AUTOMATION_CONDITION_KEY,
} from "../../../../slices/AutomationSlice";
import {
  AutomationItem,
  AutomationLookupOptions,
  AutomationOptionsKey,
} from "./automation.constants";
import {
  formatDate,
  getActionConfig,
  getConditionConfig,
  getDateValueDays,
  getDateValueDirection,
  getTemplateEmailAddress,
  renderActionSummary,
  renderConditionSummary,
  resolveAutomationValueLabel,
  resolveOptionLabel,
} from "./automation.helpers";

type Props = {
  automation: AutomationItem;
  workName: string;
  options: AutomationLookupOptions;
  isDeleting: boolean;
  workEntityLabel?: string;
  hideWorkItemTitle?: boolean;
  sourceText?: string;
  onDelete: (automation: AutomationItem) => void;
};

const getRunStatusText = (automation: AutomationItem) => {
  if (automation.lastRunStatus === "success" && automation.lastRunAt) {
    return `Successfully ran on ${formatDate(automation.lastRunAt)}`;
  }

  if (automation.lastRunStatus === "failed" && automation.lastRunAt) {
    return `Failed on ${formatDate(automation.lastRunAt)}`;
  }

  return "Not run yet";
};

const getAutomationTypeTitle = (automation: AutomationItem) => {
  return automation.scope === "subtask"
    ? "Subtask Automation"
    : "Work Automation";
};

const renderBoldValue = (value: string) => {
  return <span className={"font-semibold"}>{value}</span>;
};

export const AutomationCard: FC<Props> = ({
  automation,
  workName,
  options,
  isDeleting,
  workEntityLabel = "work item",
  hideWorkItemTitle,
  sourceText,
  onDelete,
}) => {
  const getOptionLabel = (
    optionsKey: AutomationOptionsKey,
    value: AutomationItem["conditionValue"] | AutomationItem["actionValue"],
    fallback = "",
  ) => {
    if (typeof value === "string" || value === undefined) {
      return resolveOptionLabel(options, optionsKey, value, fallback);
    }

    return resolveAutomationValueLabel(options, optionsKey, value, fallback);
  };

  const renderAutomationEntityText = () => {
    if (automation.scope === "subtask") {
      return (
        <>
          subtask &quot;
          {renderBoldValue(automation.subtaskTitle ?? "Selected subtask")}
          &quot;
        </>
      );
    }

    if (hideWorkItemTitle) {
      return <>{workEntityLabel}</>;
    }

    return (
      <>
        {workEntityLabel}{" "}
        {renderBoldValue(automation.workItemTitle ?? workName)}
      </>
    );
  };

  const renderAutomationConditionText = () => {
    switch (automation.conditionKey) {
      case AUTOMATION_CONDITION_KEY.WORK_STATUS:
        return (
          <>
            has a status of{" "}
            {renderBoldValue(
              getOptionLabel(
                "workStatuses",
                automation.conditionValue,
                "selected status",
              ),
            )}
          </>
        );

      case AUTOMATION_CONDITION_KEY.REACHED_DUE_DATE:
        return <>due date is reached</>;

      case AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE:
        return (
          <>
            reaches{" "}
            {renderBoldValue(
              getDateValueDays(automation.conditionValue) || "selected date",
            )}{" "}
            {getOptionLabel(
              "dateRules",
              getDateValueDirection(automation.conditionValue),
              "selected timing",
            )}
          </>
        );

      case AUTOMATION_CONDITION_KEY.CURRENT_SUBTASK_STATUS:
        return (
          <>
            has a status of{" "}
            {renderBoldValue(
              getOptionLabel(
                "subtaskStatuses",
                automation.conditionValue,
                "selected status",
              ),
            )}
          </>
        );

      case AUTOMATION_CONDITION_KEY.PREVIOUS_SUBTASK_STATUS:
        return (
          <>
            previous subtask has a status of{" "}
            {renderBoldValue(
              getOptionLabel(
                "subtaskStatuses",
                automation.conditionValue,
                "selected status",
              ),
            )}
          </>
        );

      case AUTOMATION_CONDITION_KEY.ALL_SUBTASKS_COMPLETED:
        return <>all previous subtasks are completed</>;

      case AUTOMATION_CONDITION_KEY.SUBTASK_REACHED_DUE_DATE:
        return <>has reached its due date</>;

      default: {
        const condition = getConditionConfig(automation.conditionKey);

        if (!condition) {
          return null;
        }

        return (
          <>
            {renderConditionSummary(
              condition,
              options,
              automation.conditionValue,
              undefined,
            )}
          </>
        );
      }
    }
  };

  const renderAutomationActionText = () => {
    switch (automation.actionKey) {
      case AUTOMATION_ACTION_KEY.ASSIGNEE:
        return (
          <>
            add{" "}
            {renderBoldValue(
              getOptionLabel(
                "users",
                automation.actionValue,
                "selected team member",
              ),
            )}{" "}
            as a team member to this{" "}
            {automation.scope === "subtask" ? "subtask" : "work item"}
          </>
        );

      case AUTOMATION_ACTION_KEY.STATUS:
        return (
          <>
            change the status to{" "}
            {renderBoldValue(
              getOptionLabel(
                "workStatuses",
                automation.actionValue,
                "selected status",
              ),
            )}
          </>
        );

      case AUTOMATION_ACTION_KEY.SEND_EMAIL: {
        const emailAddress = getTemplateEmailAddress(automation.actionValue);

        return (
          <>
            send client email{" "}
            {renderBoldValue(
              `"${getOptionLabel(
                "emailTemplates",
                automation.actionValue,
                "selected email template",
              )}"`,
            )}
            {emailAddress ? <> to {renderBoldValue(emailAddress)}</> : null}
          </>
        );
      }

      case AUTOMATION_ACTION_KEY.SEND_NOTIFICATION:
        return (
          <>
            send{" "}
            {renderBoldValue(
              getOptionLabel(
                "teamMembers",
                automation.actionValue,
                "selected team member",
              ),
            )}{" "}
            a notification
          </>
        );

      case AUTOMATION_ACTION_KEY.CHANGE_STATUS:
        return (
          <>
            change the current subtask status to{" "}
            {renderBoldValue(
              getOptionLabel(
                "subtaskStatuses",
                automation.actionValue,
                "selected status",
              ),
            )}
          </>
        );

      default: {
        const action = getActionConfig(automation.actionKey);

        if (!action) {
          return null;
        }

        return (
          <>{renderActionSummary(action, options, automation.actionValue)}</>
        );
      }
    }
  };

  const isSuccess = automation.lastRunStatus === "success";
  const isFailed = automation.lastRunStatus === "failed";

  return (
    <div className={"rounded-lg border border-gray-200 bg-white shadow-sm"}>
      <div
        className={
          "flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3"
        }>
        <div className={"flex min-w-0 items-start gap-3"}>
          <MdBolt className={"h-8 w-8 text-yellow-300"} />

          <div className={"min-w-0"}>
            <div className={"text-sm font-semibold text-[#253747]"}>
              {getAutomationTypeTitle(automation)}
            </div>

            <div className={"mt-1 text-sm leading-6 text-[#253747]"}>
              <span>When </span>
              {renderAutomationEntityText()}
              <span> </span>
              {renderAutomationConditionText()}
              {automation.scope === "subtask" ? (
                <span>, </span>
              ) : (
                <span> </span>
              )}
              {renderAutomationActionText()}
            </div>
          </div>
        </div>

        <button
          type={"button"}
          className={
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#DC2626] transition hover:bg-gray-100 disabled:opacity-60 "
          }
          disabled={isDeleting}
          onClick={() => onDelete(automation)}
          title={"Delete automation"}>
          <TrashSvg className={"h-4 w-4 "} />
        </button>
      </div>

      <div
        className={
          "flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-xs text-gray-500"
        }>
        <div>
          {sourceText || automation.sourceText ? (
            sourceText || automation.sourceText
          ) : (
            <>
              Created by{" "}
              <span className={"font-semibold text-[#253747]"}>
                {automation.createdBy}
              </span>{" "}
              on {formatDate(automation.createdAt)}
            </>
          )}
        </div>

        <div
          className={classNames(
            "font-semibold",
            isSuccess && "text-success",
            isFailed && "text-error",
            !isSuccess && !isFailed && "text-gray-500",
          )}>
          {isSuccess && "✓ "}
          {getRunStatusText(automation)}
        </div>
      </div>
    </div>
  );
};
